import React, { Component, useState, useEffect, memo } from 'react';
import { graphql } from 'gatsby'

import { Anchor, Button, DataTable, Text, Heading, FormField, Select, CheckBox } from "grommet";
import { Form, Field, FormSpy } from 'react-final-form';
import Observer from '@researchgate/react-intersection-observer';
import mermaid from 'mermaid';
import { saveAs } from 'file-saver';
import patch from "json-patch";
import { v5 as uuidv5 } from 'uuid';
import { clone, memoizeWith, identity } from "ramda";
import JSONPatchOT from 'json-patch-ot';
import produce from "immer";
import { Helmet } from "react-helmet";
import LoginComponent from "../../components/Login";

const UUID_ENTITY = 'f9b1abe2-486c-499f-b0f9-e3c49c1aae7c';
const UUID_PORTFOLIO = 'cb371132-ecef-45bc-be6f-19b74e695dcf';
const entityFullType2EntityType = {
  "Commonwealth company, limited by guarantee": "company",
  "Non-corporate Commonwealth entity": "non-corporate",
  "Corporate Commonwealth entity": "corporate",
  "Commonwealth company, limited by shares": "company",
  "Test instance - development": "test",
  "Test instance - UAT": "test",
  "Test instance - internal": "test"
};

// Transforms the latest set of patches by all rpevious ones recursivly (p1, p2(p1)->p2b, p3(p2b)->p3b, ... returns pNb)
const calculatePatch = (patches) => {
  if(!patches.length) return [];
  const basepatch = patches.shift();
  return produce(basepatch, draft => patches.reduce((against, xpatches) => xpatches.map(ptc =>JSONPatchOT.transform(ptc, against)), draft));
}
// calculateVersion(entitydatabase, [patch])
export const calculateVersion = (base, patches) => {
  const p = produce(base, draft => patches.reduce((db, op) => patch.apply(db, op), draft));
  return produce(p, draft => {
    return draft.forEach(item => {
      // modify for immerjs
      item.portfolio = item.portfolio || uuidv5(item.portfolio_name, UUID_PORTFOLIO);
      item.uuid = item.uuid || uuidv5(item.entityName, UUID_ENTITY);
      item.url = `${item.acro}.dart.masterdocs.com.au`;
      item.entityType = item.entityType || entityFullType2EntityType[item.fullType];
      item.alternative = item.alternative || [];
      item.state = item.state || "prod";
      item.decommissioned = item.decommissioned || false;
    });
  });
};



mermaid.initialize({
  theme: 'forest',
  securityLevel: 'loose',
  flowchart:{
    useMaxWidth:true,
    htmlLabels:true,
    //curve:'cardinal',
  },
  gantt: { axisFormatter: [
    ['%Y-%m-%d', (d) => {
      return d.getDay() === 1
    }]
  ] }
});

function ObserverRenderer({ children, keepRendered }) {
  const [visible, setVisible] = useState(null);
  const [forceRender, setForceRender] = useState(false);
  const options = {
    onChange: ({ isIntersecting }) => {
      setVisible(isIntersecting);
      if(keepRendered && isIntersecting) {
        setForceRender(true);
      }
    },
    rootMargin: '0% 0% 25%',
  };

  // note: Intersection observer needs the divs to hook onto
  return (<div><Observer {...options}>
    <div>{(visible || forceRender) ? children : <span/>}</div>
  </Observer></div>);
}

const MermaidRenderer = memo(({ entities }) => {
  const graphDefinition = entities.reduce((p, { portfolio_name, entityName, portfolio, acro, url }) => {

    if([portfolio_name, entityName, portfolio, acro, url].some(i => !i)) {
      throw new Error(`Missing data for ${acro}`);
    }
    return `${p}
    ${portfolio}["${portfolio_name}"] --> ${acro}["${acro}"]
    click ${acro} "http://${url}" "${entityName}"`;
  }, `graph LR`);

  const [data, setData] = useState(null);
  useEffect(() => {
    const element = document.querySelector("#graphDiv");
    const insertSvg = function(svgCode, bindFunctions){
        setData(svgCode);
    };

    const graph = mermaid.mermaidAPI.render('graphDiv', graphDefinition, insertSvg);
  }, [graphDefinition]);
  return <div dangerouslySetInnerHTML={{__html: data || "Loading...."}} />;
});


const MetaLoader = ({ accronym, children }) => {
  const [data, setData] = useState(null);
  const [intersecting, setIntersecting] = useState(false);
  const loadData = async ({ isIntersecting }) => {
    if(isIntersecting && !intersecting && !data) {
      setIntersecting(isIntersecting);
      try {
        const request = await fetch(`https://${accronym}.dart.masterdocs.com.au/frontend/version.js?${Date.now()}`);
        const data = await request.text();
        const obj = Object.fromEntries(data.split("\n").map(j => j.split("=")));
        const version = obj["APPVERSION.versionNumber"] || obj["APPVERSION.version"];
        const edata = { ...obj, version: version.slice(1,-1) };
        setData(edata);
      } catch(e) {
        console.error(e);
        setData({ version: "Warning - Entity is down!!" });
      }
    } else if(!isIntersecting) {
      setIntersecting(isIntersecting);
    }
  }


  const options = {
    onChange: loadData,
    rootMargin: '0% 0% 25%',
  };

  // note: Intersection observer needs the divs to hook onto
  return (<div key={accronym}><Observer {...options}>
    <div>{children(data || {})}</div>
  </Observer></div>);
}

const EntityName = memo(({entityName}) => (
  <Text id={entityName}>{entityName}</Text>
));
const Acro = memo(({ acro }) => <Anchor key={`Link-${acro}`} href={`https://${acro}.dart.masterdocs.com.au`} label={acro} />);
const Version = memo(({ acro }) => (<MetaLoader key={`version-${acro}`} accronym={acro}>{({ version }) =>
    (<Anchor href={`#v${version}`} label={version} />)
  }</MetaLoader>));
const Alternative = memo(({ alternative }) => alternative.length ? alternative.reduce((p, i) => <>{p}, {i}</>) : "");
const Decommissioned = memo(({ decommissioned }) => decommissioned ? "Yes" : "No")
const columns = [
  {
    property: 'entityName',
    header: <Text>Name as per MD records</Text>,
    primary: true,
    search: true,
    sortable: true,
    render: ({ entityName }) => <EntityName entityName={entityName} />,
  },
  {
    property: 'acro',
    header: <Text>Instance and md-accronym</Text>,
    search: true,
    sortable: true,
    render: ({ acro }) => <Acro acro={acro} />,
  },
  {
    property: 'entityType',
    header: <Text>Type</Text>,
    search: true,
    sortable: true
  },
  {
    property: 'fullType',
    header: <Text>Full Type</Text>,
    search: true,
    sortable: true
  },
  {
    property: 'version',
    header: <Text>App Version</Text>,
    search: true,
    sortable: true,
    render: ({ acro }) => <Version acro={acro} />,
  },
  {
    property: 'abbr',
    header: <Text>Entity used abbriviation</Text>,
    search: true,
    sortable: true
  },
  {
    property: 'alternative',
    header: <Text>Alternative names</Text>,
    search: true,
    render: ({ alternative }) => <Alternative alternative={alternative} />,
  },
  {
    property: 'portfolio_name',
    header: <Text>Belongs to portfolio</Text>,
    search: true,
    sortable: true
  },
  {
    property: 'stage',
    header: <Text>Stage</Text>,
    search: true,
    sortable: true
  },
  {
    property: 'decommissioned',
    header: <Text>Decommissioned?</Text>,
    search: true,
    sortable: true,
    render: ({ decommissioned }) => <Decommissioned decommissioned={decommissioned} />,
  },
];
const valueSubscription = { values: true };
const formSubscription = { submitting: true, pristine: true };
const noop = () => null;
const datadisplay = ({ values, state, saveEntityCSV, savePortfolioCSV }) => <>
  <Button label="Download entities_import.csv" onClick={saveEntityCSV(values.include_decommissioned, values.version)} />
  <Button label="Download portfolio_import.csv" onClick={savePortfolioCSV(values.include_decommissioned, values.version)} />
  <DataTable
    columns={columns}
    data={state.entities[values.version]
      .filter(({ decommissioned }) => {
        return !values.include_decommissioned ? !decommissioned : true;
      })
    }
  />
  <Heading level={2}>Graph View</Heading>
    <ObserverRenderer keepRendered><MermaidRenderer entities={state.entities[values.version]
      .filter(({ decommissioned }) => {
        return !values.include_decommissioned ? !decommissioned : true;
      })
    } /></ObserverRenderer>
  <Heading level={2}>Changes</Heading>
    <ObserverRenderer><pre>
      {JSON.stringify(state.availablePatches[values.version].map(({ path, comment, ...rest }) => ({
        ...rest,
        path,
        note: comment,
        comment: (e => `${e?.entityName || "New entity"} (${e?.url || ""})`)(state.entities[values.version][parseInt(path.split("/")[1])])
      })), null, "  ")}
    </pre></ObserverRenderer>
</>;

const bodyform = ({ saveEntityCSV, savePortfolioCSV, availableVersions, state }) => <>
  <Field
    name="version"
    initialValue={availableVersions[availableVersions.length-1]}
    render={({ input, meta }) => (
      <Select
        options={availableVersions}
        {...input}
        onChange={({ option }) => input.onChange(option)}
      />
    )}
  />
  <Field
    name="include_decommissioned"
    initialValue={availableVersions[0]}
    render={({ input, meta }) => (
      <CheckBox
        {...input}
        checked={input.value}
        label="Include decommissioned?"
        onChange={(event) => input.onChange(event.target.checked)}
      />
    )}
  />
  <Heading level={2}>Table</Heading>
  <FormSpy subscription={valueSubscription} state={state} saveEntityCSV={saveEntityCSV} savePortfolioCSV={savePortfolioCSV} >
    {datadisplay}
  </FormSpy>
</>;

export default class EntityPage extends Component {

  constructor(args) {
    super();
    const entitydatabase = JSON.parse(args.data.baseline.edges[0].node.childRawCode.content);
    const patches = args.data.patches.edges.map(({ node: { childRawCode: { content }, base } }) => ({content: JSON.parse(content), name: base.split(".").shift().substring(2)}));
    const [patchVersions, calculatedStates, calcualtedPatches] = patches.reduce(([allPrevious, result, patchersult], { content, name }) => [
      allPrevious.concat([content]),
      {
        ...result,
        [name]: calculateVersion(entitydatabase, [...allPrevious, content])
      },
      {
        ...patchersult,
        [name]: calculatePatch([...allPrevious, content])
      }
    ],[[], {}, {}])
    const versions = { "2019-12-01": entitydatabase, ...calculatedStates };
    const availablePatches = { "2019-12-01": [], ...calcualtedPatches };
    this.state = {
      availableVersions: versions,
      entities: versions,
      availablePatches
    };
    this.saveEntityCSV = memoizeWith((a,b) => `${a}-${b}`, this.saveEntityCSV.bind(this));
    this.savePortfolioCSV = memoizeWith((a,b) => `${a}-${b}`, this.savePortfolioCSV.bind(this));
  }


  save(filename, create) {
    const data = create();
    const blob = new Blob([data], {type: 'text/csv'});
    saveAs(blob, filename);
  }

  saveEntityCSV(include_decommissioned, version) {
    return () => this.save("entities_import", () => {
      const headers = ["uuid", "name", "description", "portfolio", "version"];
      const data = this.state.entities[version]
        .filter(({ decommissioned }) => {
          return !include_decommissioned ? !decommissioned : true;
        })
        .map(({
          uuid,
          entityName,
          portfolio
        }) => {
          const xentityName = (entityName.indexOf(',') > -1 || entityName.indexOf('"') > -1) ? `"${entityName.replace(/"/g, `""`)}"` : entityName;
          const xportfolio = (portfolio.indexOf(',') > -1 || portfolio.indexOf('"') > -1) ? `"${portfolio.replace(/"/g, `""`)}"` : portfolio;
          return [uuid, xentityName, "", xportfolio, version].join(",");
        });
      return [headers, ...data].join("\n");
    });
  }

  savePortfolioCSV(include_decommissioned, version) {
    return () => this.save("portfolio_import", () => {
      const headers = ["uuid", "name", "description", "version"];
      const portfolios = Object.fromEntries(this.state.entities[version]
        .filter(({ decommissioned }) => {
          return !include_decommissioned ? !decommissioned : true;
        })
        .map(({ portfolio_name, portfolio }) => [portfolio_name, portfolio, version]));
      const data = Object.entries(portfolios).map(([name, id]) => {
        const xname = (name.indexOf(',') > -1 || name.indexOf('"') > -1) ? `"${name.replace(/"/g, `""`)}"` : name;
        return [id, xname, ""].join(",");
      });
      return [headers, ...data].join("\n");
    });
  }

  render() {
    const availableVersions = Object.keys(this.state.availableVersions);
    return <LoginComponent>{() => (<div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Entity Overview - MD Dashboard</title>
      </Helmet>
      <Heading level={1}>Entity names Status</Heading>
      <Form
        subscription={formSubscription}
        onSubmit={noop}
        saveEntityCSV={this.saveEntityCSV}
        savePortfolioCSV={this.savePortfolioCSV}
        availableVersions={availableVersions}
        state={this.state}
        render={bodyform}
      />
    </div>)}</LoginComponent>;
  }
}

export const query = graphql`
{
  patches: allFile(filter: {base: {glob: "*.jsonpatch.json"}}) {
    ...filecontent
  }
  baseline: allFile(filter: {base: {eq: "entitydatabase.json"}}) {
    ...filecontent
  }
}

fragment filecontent on FileConnection {
  edges {
    node {
      base
      name
      childRawCode {
        content
      }
    }
  }
}
`
